import { useEffect, useMemo, useState } from 'react'

import { adminWorkflowApi } from '../services/adminApi'
import type { AdminWorkflowArticle, WorkflowRequestForm } from '../types/admin.types'
import type { ApiError, SnackbarState } from '../../../shared/types/common'

type WorkflowDialogState = {
  open: boolean
  articleId: number | null
}

const DEFAULT_REQUEST_FORM: WorkflowRequestForm = {
  request_type: 'Technical correction required',
  comment: '',
  deadline: '',
}

export function useAdminWorkflow() {
  const [articles, setArticles] = useState<AdminWorkflowArticle[]>([])
  const [metricArticles, setMetricArticles] = useState<AdminWorkflowArticle[]>([])
  const [stateFilter, setStateFilter] = useState('review')
  const [loading, setLoading] = useState(true)
  const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', severity: 'success' })
  const [requestDialog, setRequestDialog] = useState<WorkflowDialogState>({ open: false, articleId: null })
  const [requestForm, setRequestForm] = useState<WorkflowRequestForm>(DEFAULT_REQUEST_FORM)
  const [rejectDialog, setRejectDialog] = useState<WorkflowDialogState>({ open: false, articleId: null })
  const [rejectComment, setRejectComment] = useState('')
  const [archiveDialog, setArchiveDialog] = useState<WorkflowDialogState>({ open: false, articleId: null })

  const metrics = useMemo(
    () => ({
      draft: metricArticles.filter(article => article.state === 'draft').length,
      review: metricArticles.filter(article => article.state === 'review').length,
      changes_requested: metricArticles.filter(article => article.state === 'changes_requested').length,
      approved: metricArticles.filter(article => article.state === 'approved').length,
      published: metricArticles.filter(article => article.state === 'published').length,
    }),
    [metricArticles],
  )

  useEffect(() => {
    void fetchArticles()
  }, [stateFilter])

  async function fetchArticles() {
    setLoading(true)
    try {
      const params = stateFilter ? { state: stateFilter } : {}
      const [tableResponse, metricsResponse] = await Promise.all([
        adminWorkflowApi.listArticles(params),
        adminWorkflowApi.listArticles(),
      ])
      setArticles(tableResponse.data)
      setMetricArticles(metricsResponse.data)
    } catch {
      showSnackbar('Failed to load technotes', 'error')
    } finally {
      setLoading(false)
    }
  }

  async function handleApprove(id: number) {
    try {
      await adminWorkflowApi.approveArticle(id)
      showSnackbar('Technote approved')
      await fetchArticles()
    } catch (error) {
      showSnackbar((error as ApiError).response?.data?.message || 'Failed to approve', 'error')
    }
  }

  async function handleAssignReviewer(id: number, reviewerId: number) {
    try {
      await adminWorkflowApi.assignArticleReviewer(id, reviewerId)
      showSnackbar('Article assigned to you for review')
      await fetchArticles()
    } catch (error) {
      showSnackbar((error as ApiError).response?.data?.message || 'Failed to assign reviewer', 'error')
    }
  }

  async function handleReject() {
    if (!rejectDialog.articleId) {
      return
    }

    try {
      await adminWorkflowApi.rejectArticle(rejectDialog.articleId, rejectComment)
      showSnackbar('Technote returned to draft')
      setRejectDialog({ open: false, articleId: null })
      setRejectComment('')
      await fetchArticles()
    } catch (error) {
      showSnackbar((error as ApiError).response?.data?.message || 'Failed to reject', 'error')
    }
  }

  async function handleRequestChanges() {
    if (!requestDialog.articleId) {
      return
    }

    try {
      await adminWorkflowApi.requestArticleChanges(requestDialog.articleId, requestForm)
      showSnackbar('Change request sent to author')
      setRequestDialog({ open: false, articleId: null })
      setRequestForm(DEFAULT_REQUEST_FORM)
      await fetchArticles()
    } catch (error) {
      showSnackbar((error as ApiError).response?.data?.message || 'Failed to request changes', 'error')
    }
  }

  async function handlePublish(id: number) {
    try {
      await adminWorkflowApi.publishArticle(id)
      showSnackbar('Technote published')
      await fetchArticles()
    } catch (error) {
      showSnackbar((error as ApiError).response?.data?.message || 'Failed to publish', 'error')
    }
  }

  async function handleArchive() {
    if (!archiveDialog.articleId) {
      return
    }

    try {
      await adminWorkflowApi.archiveArticle(archiveDialog.articleId)
      showSnackbar('Technote archived and removed from Wiki.js access')
      setArchiveDialog({ open: false, articleId: null })
      await fetchArticles()
    } catch (error) {
      showSnackbar((error as ApiError).response?.data?.message || 'Failed to archive technote', 'error')
    }
  }

  function showSnackbar(message: string, severity: SnackbarState['severity'] = 'success') {
    setSnackbar({ open: true, message, severity })
  }

  return {
    articles,
    archiveDialog,
    handleAssignReviewer,
    handleApprove,
    handleArchive,
    handlePublish,
    handleReject,
    handleRequestChanges,
    loading,
    metrics,
    rejectComment,
    rejectDialog,
    requestDialog,
    requestForm,
    setRejectComment,
    setArchiveDialog,
    setRejectDialog,
    setRequestDialog,
    setRequestForm,
    setSnackbar,
    setStateFilter,
    snackbar,
    stateFilter,
  }
}
